import { IconType } from 'core-ui';

import { t } from '../../services/translate';
import { ReplacementVehicleType } from '../replacement-transport/replacement-transport.interface';
import { DayOptionId } from './calendar.interface';

// The ways an appointment can take place on one date — GET /wp/planner/calendar/appointment-options.
// Options that cannot be booked are left out of the response entirely.
export const APPOINTMENT_OPTION_TYPES = ['wait', 'dropOff', 'collectDeliver'] as const;

export type AppointmentOptionType = (typeof APPOINTMENT_OPTION_TYPES)[number];

interface AppointmentOptionBase {
  type: AppointmentOptionType;
  times: string[];
  // Only wait/dropOff carry these; collectDeliver is derived from `times`.
  firstTime?: string;
  numberOfTimes?: number;
}

export interface WaitAppointmentOption extends AppointmentOptionBase {
  type: 'wait';
  waitingTime: number | null;
  bike: boolean;
}

export interface DropOffAppointmentOption extends AppointmentOptionBase {
  type: 'dropOff';
  replacementVehicle: boolean;
  bike: boolean;
}

export interface CollectDeliverAppointmentOption extends AppointmentOptionBase {
  type: 'collectDeliver';
}

export type AppointmentOption =
  | WaitAppointmentOption
  | DropOffAppointmentOption
  | CollectDeliverAppointmentOption;

export interface AppointmentOptions {
  options: AppointmentOption[];
}

export function isAppointmentOptionType(value: unknown): value is AppointmentOptionType {
  return APPOINTMENT_OPTION_TYPES.some((type: AppointmentOptionType): boolean => type === value);
}

// collectDeliver ships times only, so read both through these instead of the raw fields.
export function optionFirstTime(option: AppointmentOption): string | null {
  return option.firstTime ?? option.times[0] ?? null;
}

export function optionTimeCount(option: AppointmentOption): number {
  return option.numberOfTimes ?? option.times.length;
}

// Which replacement-vehicle categories the chosen option may pick from. Waiting only ever offers
// a loan bike; collect & deliver is its own category; drop-off follows the two flags.
export function offeredVehicleTypes(option: AppointmentOption): ReplacementVehicleType[] {
  switch (option.type) {
    case 'wait':
      return option.bike ? ['bike'] : [];
    case 'dropOff':
      return [
        ...(option.replacementVehicle ? (['replacementVehicle'] as const) : []),
        ...(option.bike ? (['bike'] as const) : []),
      ];
    case 'collectDeliver':
      // The haal- en brengservice IS the transport: its own category, preselected rather than asked.
      return ['collectDeliver'];
  }
}

// What the card advertises as emphasis badges (Figma 10124:77279): the option's own mobility
// first, a loan bike last. Collect & deliver carries none — the card itself is the transport.
export function cardMobility(option: AppointmentOption): DayOptionId[] {
  switch (option.type) {
    case 'wait':
      return option.bike ? ['waiting', 'bike'] : ['waiting'];
    case 'dropOff':
      return [
        ...(option.replacementVehicle ? (['replacementVehicle'] as const) : []),
        ...(option.bike ? (['bike'] as const) : []),
      ];
    case 'collectDeliver':
      return [];
  }
}

// Card copy + icon per option type, so a new type cannot compile without them.
export interface AppointmentOptionCopy {
  icon: IconType;
  title: string;
  cta: string;
  // Line under the title; waiting has none — it shows its wait estimate instead.
  note: string | null;
}

export function appointmentOptionCopy(type: AppointmentOptionType): AppointmentOptionCopy {
  const copy: Record<AppointmentOptionType, AppointmentOptionCopy> = {
    wait: {
      icon: 'couch',
      title: t('wpp.component.service-mode.waiting.title'),
      cta: t('wpp.component.service-mode.waiting.cta'),
      note: null,
    },
    dropOff: {
      icon: 'exchange-alt',
      title: t('wpp.component.service-mode.drop-off.title'),
      cta: t('wpp.component.service-mode.drop-off.cta'),
      note: null,
    },
    collectDeliver: {
      icon: 'home',
      title: t('wpp.component.service-mode.collect-deliver.title'),
      cta: t('wpp.component.service-mode.collect-deliver.cta'),
      note: null,
    },
  };
  return copy[type];
}
